import { useEffect, useState } from "react";
import { 
  Plus, Pencil, Trash2, X, Search, ArrowLeft, 
  Clock, Calendar, AlertCircle, Target, Filter,
  ChevronDown, Zap
} from "lucide-react";
import Swal from "sweetalert2";
import config from "../config";

export default function SLAPage({ handlePageChange }) {
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [filterPriority, setFilterPriority] = useState("all");

  const [form, setForm] = useState({
    timeGoal: "",
    calendar: "",
    jql: "",
    priority: "",
    condition: "",
  });

  const API = `${config.API_BASE_URL}/SLA`;

  // ===== GET =====
  const loadData = async () => {
    const res = await fetch(API);
    const json = await res.json();
    setData(json);
  };

  useEffect(() => {
    loadData();
  }, []);

  // ===== SAVE (POST / PUT) =====
  const save = async () => {
    if (!form.priority || !form.timeGoal) {
      Swal.fire("Error", "Priority and Time Goal are required", "error");
      return;
    }

    const isUpdate = data.some((x) => x.priority === form.priority);

    try {
      if (isUpdate) {
        await fetch(`${API}/${encodeURIComponent(form.priority)}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form),
        });
      } else {
        await fetch(API, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form),
        });
      }

      Swal.fire({
        icon: "success",
        title: isUpdate ? "SLA Updated!" : "SLA Created!",
        timer: 1500,
        showConfirmButton: false,
      });

      closeModal();
      loadData();
    } catch {
      Swal.fire("Error", "Something went wrong", "error");
    }
  };

  // ===== DELETE =====
  const remove = async (priority) => {
    const result = await Swal.fire({
      title: "Delete SLA?",
      text: `This will remove the SLA for ${priority} priority`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel"
    });
    if (!result.isConfirmed) return;

    await fetch(`${API}/${encodeURIComponent(priority)}`, {
      method: "DELETE",
    });

    Swal.fire({
      icon: "success",
      title: "Deleted!",
      timer: 1200,
      showConfirmButton: false,
    });

    loadData();
  };

  const openCreate = () => {
    setForm({
      timeGoal: "",
      calendar: "",
      jql: "",
      priority: "",
      condition: "",
    });
    setShowModal(true);
  };

  const openEdit = (item) => {
    setForm(item);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setForm({
      timeGoal: "",
      calendar: "",
      jql: "",
      priority: "",
      condition: "",
    });
  };

  // ===== PRIORITY COLORS =====
  const getPriorityColor = (priority) => {
    const p = priority.toLowerCase();
    if (p.includes("critical") || p.includes("urgent")) return "red";
    if (p.includes("high")) return "orange";
    if (p.includes("medium") || p.includes("normal")) return "yellow";
    if (p.includes("low")) return "green";
    return "blue";
  };

  const getPriorityStyles = (priority) => {
    const color = getPriorityColor(priority);
    const styles = {
      red: "bg-red-500/20 text-red-400 border-red-500/30",
      orange: "bg-orange-500/20 text-orange-400 border-orange-500/30",
      yellow: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      green: "bg-green-500/20 text-green-400 border-green-500/30",
      blue: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    };
    return styles[color];
  };

  const getPriorityIcon = (priority) => {
    const color = getPriorityColor(priority);
    if (color === "red") return <Zap className="w-5 h-5" />;
    if (color === "orange") return <AlertCircle className="w-5 h-5" />;
    return <Target className="w-5 h-5" />;
  };

  // ===== SEARCH & FILTER =====
  const filteredData = data.filter((x) => {
    const matchesSearch =
      x.priority.toLowerCase().includes(search.toLowerCase()) ||
      x.timeGoal.toLowerCase().includes(search.toLowerCase()) ||
      x.condition.toLowerCase().includes(search.toLowerCase());

    const matchesFilter =
      filterPriority === "all" ||
      x.priority.toLowerCase().includes(filterPriority.toLowerCase());

    return matchesSearch && matchesFilter;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-slate-200 p-4 md:p-8">
      {/* HEADER */}
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div className="flex items-center gap-3">
            <button
              onClick={() => handlePageChange("maintenance")}
              className="p-2 rounded-lg bg-slate-800/80 hover:bg-slate-700 border border-slate-700 transition-all"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                SLA Management
              </h1>
              <p className="text-sm text-slate-400 mt-1">
                Service Level Agreement Configuration
              </p>
            </div>
          </div>

          <button
            onClick={openCreate}
            className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 px-5 py-2.5 rounded-lg font-medium shadow-lg shadow-blue-500/30 transition-all"
          >
            <Plus size={18} />
            Create SLA
          </button>
        </div>

        {/* SEARCH & FILTER BAR */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 text-slate-400" size={18} />
            <input
              className="w-full pl-10 pr-4 py-2.5 bg-slate-800/80 border border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
              placeholder="Search by priority, time goal, or condition..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="relative">
            <Filter className="absolute left-3 top-3 text-slate-400" size={18} />
            <select
              className="pl-10 pr-10 py-2.5 bg-slate-800/80 border border-slate-700 rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500/50 cursor-pointer min-w-[200px]"
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
            >
              <option value="all">All Priorities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
            <ChevronDown className="absolute right-3 top-3 text-slate-400 pointer-events-none" size={18} />
          </div>
        </div>

        {/* STATS CARDS */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-800/40 backdrop-blur border border-slate-700 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-500/20 rounded-lg">
                <Target className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Total SLAs</p>
                <p className="text-2xl font-bold">{data.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-slate-800/80 to-slate-800/40 backdrop-blur border border-slate-700 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-red-500/20 rounded-lg">
                <Zap className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Critical</p>
                <p className="text-2xl font-bold">
                  {data.filter(x => x.priority.toLowerCase().includes("critical")).length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-slate-800/80 to-slate-800/40 backdrop-blur border border-slate-700 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-orange-500/20 rounded-lg">
                <AlertCircle className="w-6 h-6 text-orange-400" />
              </div>
              <div>
                <p className="text-sm text-slate-400">High Priority</p>
                <p className="text-2xl font-bold">
                  {data.filter(x => x.priority.toLowerCase().includes("high")).length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-slate-800/80 to-slate-800/40 backdrop-blur border border-slate-700 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-500/20 rounded-lg">
                <Clock className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Active</p>
                <p className="text-2xl font-bold">{filteredData.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* SLA CARDS */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredData.map((item) => (
            <div
              key={item.priority}
              className="group bg-gradient-to-br from-slate-800/80 to-slate-800/40 backdrop-blur border border-slate-700 rounded-xl p-5 hover:border-blue-500/50 transition-all hover:shadow-xl hover:shadow-blue-500/10"
            >
              {/* PRIORITY BADGE */}
              <div className="flex items-center justify-between mb-4">
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border font-semibold text-sm ${getPriorityStyles(item.priority)}`}>
                  {getPriorityIcon(item.priority)}
                  {item.priority}
                </div>

                <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => openEdit(item)}
                    className="p-2 rounded-lg bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30 transition-all"
                    title="Edit"
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    onClick={() => remove(item.priority)}
                    className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-all"
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {/* TIME GOAL */}
              <div className="flex items-center gap-3 mb-3 pb-3 border-b border-slate-700">
                <div className="p-2 bg-blue-500/10 rounded-lg">
                  <Clock className="w-5 h-5 text-blue-400" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-slate-400 mb-0.5">Time Goal</p>
                  <p className="text-lg font-semibold text-slate-200">{item.timeGoal}</p>
                </div>
              </div>

              {/* CALENDAR */}
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-purple-500/10 rounded-lg">
                  <Calendar className="w-5 h-5 text-purple-400" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-slate-400 mb-0.5">Calendar</p>
                  <p className="text-sm font-medium text-slate-300">{item.calendar || "Not set"}</p>
                </div>
              </div>

              {/* CONDITION */}
              <div className="bg-slate-900/50 rounded-lg p-3 mt-3">
                <p className="text-xs text-slate-400 mb-1">Condition</p>
                <p className="text-sm text-slate-300 leading-relaxed">
                  {item.condition || "No condition specified"}
                </p>
              </div>
            </div>
          ))}

          {filteredData.length === 0 && (
            <div className="col-span-full flex flex-col items-center justify-center py-16 text-slate-400">
              <div className="p-4 bg-slate-800/50 rounded-full mb-4">
                <Target className="w-12 h-12" />
              </div>
              <p className="text-lg font-medium mb-1">No SLAs Found</p>
              <p className="text-sm">Try adjusting your search or create a new SLA</p>
            </div>
          )}
        </div>
      </div>

      {/* MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl w-full max-w-2xl border border-slate-700 shadow-2xl">
            {/* MODAL HEADER */}
            <div className="flex items-center justify-between p-6 border-b border-slate-700">
              <div>
                <h2 className="text-2xl font-bold">
                  {form.priority ? "Update SLA" : "Create New SLA"}
                </h2>
                <p className="text-sm text-slate-400 mt-1">
                  Configure service level agreement parameters
                </p>
              </div>
              <button
                onClick={closeModal}
                className="p-2 rounded-lg hover:bg-slate-700 transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* MODAL BODY */}
            <div className="p-6 space-y-5">
              {/* PRIORITY */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium mb-2 text-slate-300">
                  <Target className="w-4 h-4" />
                  Priority Level *
                </label>
                <input
                  className="w-full bg-slate-900/80 border border-slate-700 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="e.g., Critical, High, Medium, Low"
                  value={form.priority}
                  onChange={(e) => setForm({ ...form, priority: e.target.value })}
                  disabled={!!data.find(x => x.priority === form.priority)}
                />
                {form.priority && data.find(x => x.priority === form.priority) && (
                  <p className="text-xs text-yellow-400 mt-1">Priority cannot be changed during update</p>
                )}
              </div>

              {/* TIME GOAL */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium mb-2 text-slate-300">
                  <Clock className="w-4 h-4" />
                  Time Goal *
                </label>
                <input
                  className="w-full bg-slate-900/80 border border-slate-700 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
                  placeholder="e.g., 4h, 24h, 2d"
                  value={form.timeGoal}
                  onChange={(e) => setForm({ ...form, timeGoal: e.target.value })}
                />
              </div>

              {/* CALENDAR */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium mb-2 text-slate-300">
                  <Calendar className="w-4 h-4" />
                  Calendar
                </label>
                <input
                  className="w-full bg-slate-900/80 border border-slate-700 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
                  placeholder="e.g., Business Hours, 24/7"
                  value={form.calendar}
                  onChange={(e) => setForm({ ...form, calendar: e.target.value })}
                />
              </div>

              {/* CONDITION */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium mb-2 text-slate-300">
                  <AlertCircle className="w-4 h-4" />
                  Condition / JQL
                </label>
                <textarea
                  className="w-full bg-slate-900/80 border border-slate-700 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all resize-none"
                  placeholder="Enter condition or JQL query"
                  rows={3}
                  value={form.condition}
                  onChange={(e) => setForm({ ...form, condition: e.target.value })}
                />
              </div>
            </div>

            {/* MODAL FOOTER */}
            <div className="flex justify-end gap-3 p-6 border-t border-slate-700">
              <button
                onClick={closeModal}
                className="px-5 py-2.5 rounded-lg bg-slate-700 hover:bg-slate-600 transition-all font-medium"
              >
                Cancel
              </button>
              <button
                onClick={save}
                className="px-6 py-2.5 rounded-lg bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 transition-all font-medium shadow-lg shadow-blue-500/30"
              >
                {form.priority && data.find(x => x.priority === form.priority) ? "Update SLA" : "Create SLA"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}